/**
 * Die Karte „Liefervertrag" im Zählerdetail (Spec 46c, „Beide Clients —
 * Zählerdetail") und die Abschnitte „Frühere Verträge". Rein darstellend:
 * Anbieter, Laufzeit, „kündbar bis", Resttage, Preise und Nummern kommen
 * wörtlich vom Server (`*_display`); der Client rechnet und formatiert nichts.
 *
 * Aktionen („Bearbeiten", „Liefervertrag anlegen") sieht nur, wer den Zähler
 * bearbeiten darf (`can_edit`); die Blätter öffnet `routes/Energie.tsx`.
 */

import type { ReactNode } from 'react'

import type { SupplyContract } from '@/features/energy/api'

const ACTION_CLASS =
  'border-border text-text hover:bg-bg focus-visible:border-accent rounded-card text-body flex min-h-(--tap-min) items-center border px-3 transition-colors duration-(--duration-fast) ease-(--ease-out)'

interface FactProps {
  readonly name: string
  readonly label: string
  readonly value: string | null
  readonly children?: ReactNode
}

/** Beschriftung links, Wert rechts; unter 640 px steht die Beschriftung über dem Wert. */
function Fact({ name, label, value, children }: FactProps) {
  return (
    <div className="border-border flex min-h-(--row-h) flex-col justify-center gap-0.5 border-b px-4 py-2 last:border-b-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:py-1">
      <dt className="text-label text-muted shrink-0">{label}</dt>
      <dd data-testid={`meter-supply-fact-${name}`} className="text-body text-text flex tabular-nums min-w-0 items-center gap-2 sm:justify-end sm:text-right">
        <span className="min-w-0 break-words">{value ?? '—'}</span>
        {children}
      </dd>
    </div>
  )
}

function RemainingCapsule({ testId, text }: { readonly testId: string; readonly text: string }) {
  return (
    <span
      data-numeric
      data-testid={testId}
      className="bg-status-partial-surface text-status-partial-text text-label shrink-0 rounded-full px-2 py-0.5 font-semibold whitespace-nowrap"
    >
      {text}
    </span>
  )
}

interface SupplyContractCardProps {
  /** Der laufende (oder nächste künftige) Vertrag; `null` heißt: keiner. */
  readonly contract: SupplyContract | null
  readonly canEdit: boolean
  readonly onEdit: (contract: SupplyContract) => void
  readonly onAdd: () => void
}

export function SupplyContractCard({ contract, canEdit, onEdit, onAdd }: SupplyContractCardProps) {
  if (contract === null) {
    return (
      <section data-testid="meter-supply-card" className="border-border bg-surface rounded-card flex flex-col gap-2 border p-4">
        <h3 className="text-section font-(--font-weight-section) text-text">Liefervertrag</h3>
        <p data-testid="meter-supply-empty" className="text-body text-muted">
          Kein Liefervertrag.
        </p>
        {canEdit && (
          <button type="button" data-testid="supply-add-empty" onClick={onAdd} className={`${ACTION_CLASS} self-start`}>
            Liefervertrag anlegen
          </button>
        )}
      </section>
    )
  }

  return (
    <section data-testid="meter-supply-card" className="border-border bg-surface rounded-card border">
      <header className="border-border flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2">
        <div className="flex min-w-0 flex-col">
          <h3 className="text-section font-(--font-weight-section) text-text">Liefervertrag</h3>
          <span data-testid="meter-supply-period" className="text-label text-muted">
            {contract.period_display}
          </span>
        </div>
        {canEdit && (
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" data-testid="supply-add" onClick={onAdd} className={ACTION_CLASS}>
              Liefervertrag anlegen
            </button>
            <button
              type="button"
              data-testid="supply-edit"
              onClick={() => {
                onEdit(contract)
              }}
              className={ACTION_CLASS}
            >
              Bearbeiten
            </button>
          </div>
        )}
      </header>
      <dl data-testid="meter-supply-facts">
        <Fact name="provider" label="Anbieter" value={contract.provider?.label ?? contract.provider_label} />
        <Fact name="period" label="Laufzeit" value={contract.period_display} />
        <Fact name="cancel-by" label="Kündbar bis" value={contract.cancel_by_display}>
          {contract.remaining_display !== null && (
            <RemainingCapsule testId="meter-supply-remaining" text={contract.remaining_display} />
          )}
        </Fact>
        <Fact name="notice" label="Kündigungsfrist" value={contract.notice_display} />
        <Fact name="renewal" label="Verlängerung" value={contract.renewal_display} />
        <Fact name="unit-price" label="Arbeitspreis" value={contract.unit_price_display} />
        <Fact name="base-price" label="Grundpreis" value={contract.base_price_display} />
        <Fact name="installment" label="Abschlag" value={contract.installment_display} />
        <Fact name="price-guarantee" label="Preisgarantie bis" value={contract.price_guarantee_display} />
        <Fact name="customer-number" label="Kundennummer" value={contract.customer_number} />
        <Fact name="contract-number" label="Vertragsnummer" value={contract.contract_number} />
        {contract.terminated_on_display !== null && (
          <Fact name="terminated" label="Gekündigt zum" value={contract.terminated_on_display} />
        )}
      </dl>
    </section>
  )
}
